import { useState } from "react";

import { fetchJSON } from "lib/fetch";
import siteConfig from "site-config";
import { checkStatus } from "actions/base-actions";
import me from "models/me";

import { TAPIResponse, SortEnum } from "../../types";
import { API_STATES } from "../../helpers";

export type TFetchInvitationsPayload = {
    merchantId: string;
    searchParams?: {
        searchField: string;
        searchValue: string;
    }[];
    sortParams?: {
        sortBy: string;
        sortOrder: SortEnum;
    };
    paginationParams?: {
        cursor?: string;
        limit: number;
    };
};

export type TFetchInvitationsResponse = {
    invitations: {
        id: string;
        merchantId: string;
        createdBy: string;
        email: string;
        name: string;
        type: string;
        status: string;
        createdOn: string;
        updatedOn: string;
        expiresOn: string;
    }[];
    nextCursor: string;
};

export type TFetchInvitationsError = { code: number; message: string };

type TFetchInvitationsHookParams = {
    dispatch: any;
};

type TFetchInvitationsHookResponse<Params, Response> = {
    (params?: Params): Response & {
        fetchInvitations: (payload: TFetchInvitationsPayload) => Promise<TFetchInvitationsResponse>;
    };
};

export const useFetchInvitations: TFetchInvitationsHookResponse<
    TFetchInvitationsHookParams,
    TAPIResponse<TFetchInvitationsResponse, TFetchInvitationsError>
> = (hookParams: TFetchInvitationsHookParams) => {
    const [state, setState] = useState<TAPIResponse<TFetchInvitationsResponse, TFetchInvitationsError>>(
        API_STATES.default,
    );
    const APIStates = API_STATES.getStates(setState);

    const fetchInvitations = (payload: TFetchInvitationsPayload): Promise<TFetchInvitationsResponse> => {
        const URL = `${siteConfig.MERCHANT_ORGANIZATION_URL}/invitations.v1.Service/ListInvitations`;

        APIStates.pending();

        return fetchJSON<TFetchInvitationsResponse>(URL, {
            method: "POST",
            body: JSON.stringify(payload),
        })
            .then(APIStates.success)
            .catch((error) => {
                if (hookParams.dispatch) {
                    if (error.code === 401) {
                        me.logout(true);
                    }
                    checkStatus(hookParams.dispatch, error.code, null);
                }
                APIStates.error({ message: error });
            });
    };

    return {
        data: state.data,
        error: state.error,
        isLoading: state.isLoading,
        loaded: state.loaded,
        fetchInvitations,
    };
};
